import { useState } from "react";
import { Calculator, Check } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { formatPrice, productMessage, waLink } from "@/lib/store";
import { installmentFor, useRates } from "@/lib/pricing";
import { WhatsAppIcon } from "./WhatsAppIcon";

const COUNTS = Array.from({ length: 21 }, (_, i) => i + 1);
const CASH = "avista";

const payText = (p, pick) => {
  if (pick === CASH) return `à vista no Pix ou dinheiro (${formatPrice(p.price)})`;
  const each = formatPrice(installmentFor(p.price, pick).each);
  return pick === 1 ? `em 1x no cartão de crédito (${each})` : `em ${pick}x de ${each} no cartão`;
};

const Option = ({ id, active, onPick, testid, label, value, big = false }) => (
  <button type="button" role="radio" aria-checked={active} data-testid={testid} onClick={() => onPick(id)}
    className={`flex w-full items-center gap-3 rounded-2xl px-3 text-left transition-colors ${big ? "py-3" : "py-2.5"} ${active ? "bg-[#25D366]/15 ring-1 ring-[#25D366]" : "hover:bg-white/[0.06]"}`}>
    <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${active ? "border-[#25D366] bg-[#25D366] text-black" : "border-white/30"}`}>
      {active && <Check className="h-3 w-3" strokeWidth={3} />}
    </span>
    <span className="flex-1 text-sm text-zinc-300">{label}</span>
    <span className={`font-semibold text-white ${big ? "font-display text-xl" : "text-sm"}`}>{value}</span>
  </button>
);

export const InstallmentSimulator = ({ p }) => {
  const [open, setOpen] = useState(false);
  const [pick, setPick] = useState(null);
  useRates();
  if (!(Number(p?.price) > 0)) return null;
  const href = pick ? waLink(productMessage(p, "", payText(p, pick))) : undefined;
  return (
    <>
      <button data-testid="installment-simulator-button" onClick={() => setOpen(true)}
        className="mt-3 inline-flex items-center gap-2 rounded-full border border-white/20 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-white/10 active:scale-95">
        <Calculator className="h-4 w-4" /> Simular parcelas
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="installment-simulator" className="max-h-[90vh] w-[calc(100%-2rem)] max-w-md gap-0 overflow-hidden rounded-[28px] border-white/10 bg-[#0d0d0f] p-0 text-white">
          <div className="border-b border-white/10 px-6 pb-4 pt-6">
            <DialogTitle className="font-display text-2xl font-bold text-white">Simular parcelas</DialogTitle>
            <DialogDescription className="mt-1 text-sm text-zinc-400">{p.model}{p.storage ? ` · ${p.storage}` : ""} — escolha como quer pagar.</DialogDescription>
            <div className="mt-4">
              <Option id={CASH} big active={pick === CASH} onPick={setPick} testid="simulator-cash-option" label="À vista (Pix ou dinheiro)" value={<span data-testid="simulator-cash-price">{formatPrice(p.price)}</span>} />
            </div>
          </div>
          <div data-lenis-prevent data-testid="simulator-list" role="radiogroup" className="max-h-[42vh] space-y-0.5 overflow-y-auto px-3 py-2">
            {COUNTS.map((n) => (
              <Option key={n} id={n} active={pick === n} onPick={setPick} testid={`simulator-row-${n}x`}
                label={n === 1 ? "1x no crédito" : `${n}x de`} value={formatPrice(installmentFor(p.price, n).each)} />
            ))}
          </div>
          <div className="border-t border-white/10 px-6 py-4">
            <a data-testid="simulator-whatsapp-button" data-pid={p.id} data-pmodel={p.model} href={href} target="_blank" rel="noopener noreferrer" aria-disabled={!pick}
              onClick={(e) => !pick && e.preventDefault()}
              className={`flex w-full items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-semibold transition-transform ${pick ? "bg-[#25D366] text-black hover:scale-[1.02] active:scale-95" : "cursor-not-allowed bg-white/10 text-zinc-400"}`}>
              <WhatsAppIcon className="h-4 w-4" />
              {pick ? (pick === CASH ? "Pedir à vista no WhatsApp" : `Pedir em ${pick}x no WhatsApp`) : "Escolha uma forma de pagamento"}
            </a>
            <p className="mt-2 text-center text-xs text-zinc-400">Parcelamento no cartão de crédito. Valores confirmados no WhatsApp.</p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
